/**
 * Bilateral Filter — edge-preserving smoothing.
 *
 *     B(p) = (1/W) · Σ_q G_σs(|p − q|) · G_σr(|I_p − I_q|) · I_q
 *     W    =        Σ_q G_σs(|p − q|) · G_σr(|I_p − I_q|)
 *
 * Two Gaussians: spatial (σs) decays with pixel distance, range (σr)
 * decays with intensity difference. The product gives high weight only
 * to neighbours that are BOTH close in space AND close in colour — so
 * regions on either side of a high-contrast edge are smoothed
 * independently, leaving the edge sharp.
 *
 * Complexity trap (per the Wave 11.3 brief): O(N · K²) naive — K = the
 * spatial window diameter. We CAP σs at 5 (→ K = 31) to stay inside
 * the 4K perf budget (< 500 ms at σs = 5). For larger smoothing the
 * recommended path is `BlrrdHDR.guidedFilter`, which is O(N) regardless
 * of radius — already shipped.
 *
 * Pipeline slot: destructive pre-kernel commit. Identity short-circuit:
 * σs ≤ 0 OR σr ≤ 0 → no-op.
 *
 * References:
 *   • Tomasi, C. & Manduchi, R. (1998) "Bilateral Filtering for Gray
 *     and Color Images", ICCV 1998.
 *
 * License: BLRRD is MIT. NO GPLv3 source consulted.
 *
 * @module src/pipeline/filters/spatial/bilateral
 */

import { mirrorIndex, validateRGBA, LUMA_R, LUMA_G, LUMA_B } from './_util.js';
import { isGPUAvailable, gpuBilateral } from './_gpu.js';
import { BILATERAL_KMAX } from './shaders/bilateral.glsl.js';

/** Hard cap on σs — see header notes (perf budget). */
export const BILATERAL_SIGMA_SPATIAL_MAX = 5;
export const BILATERAL_SIGMA_SPATIAL_MIN = 0.5;
/** Range σ is in linear units of the input (scene-linear ACEScg). */
export const BILATERAL_SIGMA_RANGE_MAX   = 0.5;
export const BILATERAL_SIGMA_RANGE_MIN   = 0.01;

const IDENTITY_EPS = 1e-6;

/**
 * Default Bilateral parameters. Identity (σr = 0 → fall back to range-
 * unaware Gaussian, but we short-circuit when σs is also at the floor).
 *
 * Note: we use σr = 0 as the identity sentinel; the slider's interactive
 * range starts at SIGMA_RANGE_MIN.
 *
 * @returns {{ sigmaSpatial: number, sigmaRange: number }}
 */
export function defaultParams() {
    return { sigmaSpatial: 0, sigmaRange: 0 };
}

/**
 * Identity test for the default-params short-circuit.
 *
 * @param {{ sigmaSpatial?: number, sigmaRange?: number }} params
 * @returns {boolean}
 */
export function isIdentityBilateral(params) {
    if (!params) return true;
    const ss = +params.sigmaSpatial;
    const sr = +params.sigmaRange;
    if (!Number.isFinite(ss) || !Number.isFinite(sr)) return true;
    if (ss <= IDENTITY_EPS) return true;
    if (sr <= IDENTITY_EPS) return true;
    return false;
}

/**
 * Pre-compute the 2D spatial Gaussian kernel as a flat (2r+1)×(2r+1)
 * Float32Array indexed by (k+r) * (2r+1) + (j+r). The kernel is
 * symmetric and unit-sum is NOT enforced here — bilateral normalises
 * per-pixel via the per-window W denominator.
 *
 * @param {number} sigmaSpatial
 * @param {number} r            kernel radius (= ceil(3·σs))
 * @returns {Float32Array}      length (2r+1)²
 */
function buildSpatialKernel(sigmaSpatial, r) {
    const K = 2 * r + 1;
    const inv2s2 = 1 / (2 * sigmaSpatial * sigmaSpatial);
    const out = new Float32Array(K * K);
    for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
            out[(dy + r) * K + (dx + r)] = Math.exp(-(dx * dx + dy * dy) * inv2s2);
        }
    }
    return out;
}

/**
 * Apply Bilateral filter in-place on a packed RGBA Float32 buffer.
 *
 * Algorithm:
 *   • Range distance uses Rec.709 luminance — one scalar per pixel,
 *     not three. Faster and more stable than per-channel-bilateral
 *     (which can drift colours at edges).
 *   • For each pixel:
 *        Sum colour over a 2r+1 window, weighting each neighbour by
 *        spatial_gaussian × range_gaussian(|luma_p − luma_q|).
 *   • Mirror-extend boundary.
 *
 * @param {Float32Array} rgba    scene-linear RGBA, length 4·w·h
 * @param {number} w
 * @param {number} h
 * @param {object} params
 * @param {number} params.sigmaSpatial   [0.5, 5]
 * @param {number} params.sigmaRange     [0.01, 0.5]
 * @returns {Float32Array}       same `rgba` reference (in-place)
 */
export function applyBilateral(rgba, w, h, params) {
    validateRGBA('applyBilateral', rgba, w, h);
    const p = params || {};
    if (isIdentityBilateral(p)) return rgba;

    const sigmaSpatial = Math.min(
        BILATERAL_SIGMA_SPATIAL_MAX,
        Math.max(BILATERAL_SIGMA_SPATIAL_MIN, +p.sigmaSpatial),
    );
    const sigmaRange = Math.min(
        BILATERAL_SIGMA_RANGE_MAX,
        Math.max(BILATERAL_SIGMA_RANGE_MIN, +p.sigmaRange),
    );

    const r = Math.max(1, Math.ceil(3 * sigmaSpatial));
    const K = 2 * r + 1;
    const spatial = buildSpatialKernel(sigmaSpatial, r);
    const inv2sr2 = 1 / (2 * sigmaRange * sigmaRange);

    /* Wave 11.4 GPU path. The shader runs the O(K²) bilateral kernel
     * in a single fragment pass. r capped at BILATERAL_KMAX matches
     * the CPU SIGMA_SPATIAL_MAX cap. On GPU failure (init, shader
     * compile, FBO incomplete, context-lost) we silently fall through
     * to the CPU path below — correctness identical, speed differs. */
    if (r <= BILATERAL_KMAX && isGPUAvailable()) {
        try {
            const out = gpuBilateral(rgba, w, h, spatial, r, inv2sr2);
            rgba.set(out);
            return rgba;
        } catch (err) {
            if (typeof globalThis !== 'undefined' && globalThis.BLRRD_DEBUG_GPU) {
                /* eslint-disable-next-line no-console */
                console.warn('[bilateral] GPU path failed, falling back to CPU:', err);
            }
        }
    }

    /* Read-only copy: we sample from src while writing dst. */
    const src = new Float32Array(rgba);

    /* Pre-compute luma per pixel for cheap range lookups. */
    const N = w * h;
    const lumaMap = new Float32Array(N);
    for (let i = 0, j = 0; i < N; i++, j += 4) {
        lumaMap[i] = LUMA_R * src[j] + LUMA_G * src[j + 1] + LUMA_B * src[j + 2];
    }

    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            const cIdx = y * w + x;
            const cLuma = lumaMap[cIdx];
            let sumR = 0, sumG = 0, sumB = 0, sumW = 0;
            for (let dy = -r; dy <= r; dy++) {
                const ny = mirrorIndex(y + dy, h);
                const rowOff = ny * w;
                const kRow = (dy + r) * K;
                for (let dx = -r; dx <= r; dx++) {
                    const nx = mirrorIndex(x + dx, w);
                    const idx = rowOff + nx;
                    const ls = lumaMap[idx];
                    const dl = ls - cLuma;
                    const ws = spatial[kRow + (dx + r)];
                    const wr = Math.exp(-dl * dl * inv2sr2);
                    const wgt = ws * wr;
                    const off = idx * 4;
                    sumR += src[off]     * wgt;
                    sumG += src[off + 1] * wgt;
                    sumB += src[off + 2] * wgt;
                    sumW += wgt;
                }
            }
            const inv = sumW > 0 ? 1 / sumW : 0;
            const j = cIdx * 4;
            rgba[j]     = sumR * inv;
            rgba[j + 1] = sumG * inv;
            rgba[j + 2] = sumB * inv;
            /* alpha untouched. */
        }
    }
    return rgba;
}
